import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { nanoid } from 'nanoid';
import classNames from 'classnames';

import {
  LightningBoltIcon,
  PlusSmIcon,
  SparklesIcon,
} from '@heroicons/react/outline';
import Spinner from '../../svgs/Spinner';
import Button from '../../components/Button';
import PageContainer from '../../components/Containers/PageContainer';
import Tabs from '../../components/Tabs';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import { customToast } from '../../components/ToastAlert/customToast';
import { logger } from '../../../lib/logger';
import { isProviderUserOwnerOrAdminAuthorized } from '../../../lib/auth';
import { useAuth } from '../../../contexts/AuthContext';

import {
  Feature,
  useAutomationRulesQuery,
  useCreateAutomationRuleMutation,
  useUpdateAutomationRuleMutation,
  useDeleteAutomationRuleMutation,
  useEnableAutomationRuleMutation,
  useDisableAutomationRuleMutation,
  useMeProviderProgramTagsQuery,
  useAutomationExecutionsQuery,
  CreateAutomationRuleInput,
  UpdateAutomationRuleInput,
  AutomationRuleTriggerEvent,
} from '../../../generated/graphql';

import { useFeatureFlags } from '../../../contexts/FeatureFlagContext';
import { isExpectedAutomationError } from './isExpectedAutomationError';
import { DEFAULT_RULE } from './constants';
import { POPULAR_RECIPES, type PopularRecipe } from './popularRecipes';
import PopularRecipeCard from './PopularRecipeCard';
import {
  AutomationRuleForm,
  AutomationRule,
  AutomationRulePartial,
} from './types';
import RuleList from './RuleList';
import RuleEditor from './RuleEditor';
import ExecutionsTab from './ExecutionsTab';
import Modal from '../../components/Modal';
import ConfigurationTab from './ConfigurationTab';
import Templates from './Templates';

const Automations = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authedProviderUser, isAuthedProviderUserLoading, showUpgradeBanner } =
    useAuth();
  const {
    enabledFeatures,
    loading: featuresLoading,
    error: featuresError,
    isFeatureEnabled,
  } = useFeatureFlags();
  const featuresLoaded = enabledFeatures !== undefined;
  const isMailSequencesEnabled =
    !featuresError && isFeatureEnabled(Feature.MailSequences);

  const isAuthorized =
    !isAuthedProviderUserLoading &&
    isProviderUserOwnerOrAdminAuthorized(authedProviderUser);

  useEffect(() => {
    if (isAuthedProviderUserLoading) return;
    if (!isAuthorized) {
      navigate('/', { replace: true });
    }
  }, [isAuthorized, isAuthedProviderUserLoading, navigate]);

  // Selected rule state (null = no rule selected)
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [isRuleEditorModalOpen, setIsRuleEditorModalOpen] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleteTargetRuleId, setDeleteTargetRuleId] = useState<string | null>(
    null,
  );

  // Saving refetch state - prevents double-save while refetching after successful mutation
  const [isSavingRefetch, setIsSavingRefetch] = useState(false);

  // Newly created tags - tracks tags created during this session so they display immediately
  const [newlyCreatedTags, setNewlyCreatedTags] = useState<
    Record<string, { id: string; name: string; color?: string | null }>
  >({});

  // Track the tab where user clicked "Create automation" to navigate back on cancel, or to automations on success
  const [createSourceTab, setCreateSourceTab] = useState<string | null>(null);

  // Track whether we've already auto-switched to automations section (only do it once)
  const hasAutoSwitchedSectionRef = useRef(false);

  // Preserve automationRuleId from navigation state until rules are loaded
  const pendingAutomationRuleIdRef = useRef<string | null>(null);

  // Active tab section: 'recipes' | 'automations'
  // Default to 'recipes', but switch to 'automations' if any automation rules exist
  const [activeSection, setActiveSection] = useState<'recipes' | 'automations'>(
    'recipes',
  );

  // Fade overlay: only visible when content is scrolled down
  const [showFade, setShowFade] = useState(false);
  useEffect(() => {
    setShowFade(false);
  }, [activeSection]);

  const {
    watch,
    setValue,
    reset: resetForm,
  } = useForm<AutomationRuleForm>({
    defaultValues: {
      automationRules: [],
    },
  });

  const {
    data: automationRulesData,
    loading: automationRulesLoading,
    error: automationRulesError,
    refetch: refetchAutomationRules,
  } = useAutomationRulesQuery({
    variables: { limit: 100 },
  });
  const { refetch: refetchProgramTags } = useMeProviderProgramTagsQuery();

  const [createAutomationRuleMutation, { loading: creating }] =
    useCreateAutomationRuleMutation();
  const [updateAutomationRuleMutation, { loading: updating }] =
    useUpdateAutomationRuleMutation();
  const [deleteAutomationRuleMutation, { loading: deleting }] =
    useDeleteAutomationRuleMutation();
  const [enableAutomationRuleMutation] = useEnableAutomationRuleMutation();
  const [disableAutomationRuleMutation] = useDisableAutomationRuleMutation();
  const { data: executionsData, error: executionsError } =
    useAutomationExecutionsQuery({
      variables: { limit: 500 },
      fetchPolicy: 'cache-and-network',
    });

  const watchRules = watch('automationRules');
  const isHistoryTab = location.pathname.endsWith('/history');
  const isConfigurationTab = location.pathname.endsWith('/configuration');
  const isTemplatesTab =
    location.pathname.includes('/automations/templates') ||
    location.pathname.endsWith('/mail-sequence');
  const executionStatsByRuleId = useMemo(() => {
    const stats: Record<
      string,
      {
        total: number;
        success: number;
        failure: number;
        skipped: number;
        lastRunAt: string | null;
      }
    > = {};
    const rows = executionsData?.automationExecutions || [];

    for (const row of rows) {
      const ruleId = row.automationRuleId;
      if (!ruleId) continue;

      if (!stats[ruleId]) {
        stats[ruleId] = {
          total: 0,
          success: 0,
          failure: 0,
          skipped: 0,
          lastRunAt: null,
        };
      }

      stats[ruleId].total += 1;
      if (row.status === 'success') stats[ruleId].success += 1;
      if (row.status === 'failure') stats[ruleId].failure += 1;
      if (row.status === 'skipped') stats[ruleId].skipped += 1;

      if (row.startedAt) {
        if (
          !stats[ruleId].lastRunAt ||
          new Date(row.startedAt) > new Date(stats[ruleId].lastRunAt as string)
        ) {
          stats[ruleId].lastRunAt = row.startedAt;
        }
      }
    }

    return stats;
  }, [executionsData?.automationExecutions]);

  useEffect(() => {
    if (
      location.pathname === '/automations' ||
      location.pathname === '/automations/'
    ) {
      navigate('/automations/rules', { replace: true });
      return;
    }
    // Legacy route → Templates > Mail Sequence Templates (or Mail when gated)
    if (location.pathname.endsWith('/automations/mail-sequence')) {
      if (featuresLoading && !featuresLoaded) {
        return;
      }
      navigate(
        isMailSequencesEnabled
          ? '/automations/templates/mail-sequence'
          : '/automations/templates/mail',
        { replace: true },
      );
      return;
    }
    if (
      location.pathname === '/automations/templates' ||
      location.pathname === '/automations/templates/'
    ) {
      navigate('/automations/templates/mail', { replace: true });
    }
  }, [
    featuresLoaded,
    featuresLoading,
    isMailSequencesEnabled,
    location.pathname,
    navigate,
  ]);

  // Switch to 'automations' section when automation rules are loaded and at least one exists
  useEffect(() => {
    if (
      !hasAutoSwitchedSectionRef.current &&
      automationRulesData?.automationRules &&
      automationRulesData.automationRules.length > 0
    ) {
      hasAutoSwitchedSectionRef.current = true;
      setActiveSection('automations');
    }
  }, [automationRulesData?.automationRules]);

  useEffect(() => {
    if (!automationRulesData?.automationRules) return;

    /**
     * Normalize rules to ensure automationRuleId is always set.
     * Server rules use automationRuleId; new local rules use 'id' (prefixed 'temp-').
     * This fallback ensures both types of rules can be handled uniformly.
     */
    const nextRules: AutomationRulePartial[] =
      automationRulesData.automationRules.map((rule) => ({
        ...rule,
        automationRuleId: rule.automationRuleId || rule.id,
      }));

    // Navigation state from routing deep-links:
    // - automationRuleId: Open the editor for this existing rule
    // - copyFromRuleId: Create a new rule as a copy of this existing rule
    const state = location.state as {
      automationRuleId?: string;
      copyFromRuleId?: string;
    } | null;

    if (state?.copyFromRuleId) {
      const ruleToCopy = automationRulesData.automationRules.find(
        (r) => r.id === state.copyFromRuleId,
      );
      if (ruleToCopy) {
        const copiedRule: AutomationRulePartial = {
          id: `temp-${nanoid()}`,
          name: `Copy of ${ruleToCopy.name}`,
          description: ruleToCopy.description || '',
          enabled: true,
          triggerEvent: ruleToCopy.triggerEvent,
          triggerConfig: ruleToCopy.triggerConfig,
          priority: ruleToCopy.priority,
          conditions: ruleToCopy.conditions,
          actions: ruleToCopy.actions,
        };

        nextRules.unshift(copiedRule);
        setSelectedRuleId(copiedRule.id!);
      }
    }

    setValue('automationRules', nextRules);

    if (state?.automationRuleId) {
      setSelectedRuleId(state.automationRuleId);
    }
  }, [automationRulesData]);

  // Handle navigation state for deep-linking to automations
  useEffect(() => {
    if (!location.state) return;
    if (location.pathname.endsWith('/history')) return;

    const state = location.state as {
      automationRuleId?: string;
      copyFromRuleId?: string;
    } | null;

    // Handle automationRuleId deep-link
    if (state?.automationRuleId) {
      if (automationRulesData?.automationRules) {
        // Rules are loaded, process immediately
        const rule = automationRulesData.automationRules.find(
          (r) =>
            r.id === state.automationRuleId ||
            r.automationRuleId === state.automationRuleId,
        );
        if (rule) {
          setSelectedRuleId(rule.id);
          setIsRuleEditorModalOpen(true);
          pendingAutomationRuleIdRef.current = null;
        }
      } else {
        // Rules not loaded yet, preserve the ID for later
        pendingAutomationRuleIdRef.current = state.automationRuleId;
      }
    }

    // Clear the state after processing (or preserving)
    navigate('.', { replace: true, state: null });
  }, [
    location.state,
    location.pathname,
    automationRulesData?.automationRules,
    navigate,
  ]);

  // When automation rules load, check if there's a pending automationRuleId to open
  useEffect(() => {
    if (
      !pendingAutomationRuleIdRef.current ||
      !automationRulesData?.automationRules
    ) {
      return;
    }

    const rule = automationRulesData.automationRules.find(
      (r) =>
        r.id === pendingAutomationRuleIdRef.current ||
        r.automationRuleId === pendingAutomationRuleIdRef.current,
    );

    if (rule) {
      setSelectedRuleId(rule.id);
      setIsRuleEditorModalOpen(true);
      pendingAutomationRuleIdRef.current = null;
    }
  }, [automationRulesData?.automationRules]);

  // Log query errors to Sentry. GraphQL errors that surface during auth
  // redirect races (e.g. /auth-callback, session clearing) are expected and
  // downgraded to warnings to avoid Sentry noise (WEB-90, WEB-8Z).
  useEffect(() => {
    if (automationRulesError) {
      if (isExpectedAutomationError(automationRulesError)) {
        logger.warn('Failed to load automation rules (auth race)', {
          error: automationRulesError,
        });
      } else {
        logger.error('Failed to load automation rules', {
          error: automationRulesError,
        });
      }
    }
  }, [automationRulesError]);

  useEffect(() => {
    if (executionsError) {
      if (isExpectedAutomationError(executionsError)) {
        logger.warn('Failed to load automation executions (auth race)', {
          error: executionsError,
        });
      } else {
        logger.error('Failed to load automation executions', {
          error: executionsError,
        });
      }
    }
  }, [executionsError]);

  const handleSelectRule = (ruleId: string) => {
    setSelectedRuleId(ruleId);
    setSaveError(null);
  };

  const handleCreateNew = () => {
    const newRule: AutomationRulePartial = {
      ...DEFAULT_RULE(),
      id: `temp-${nanoid()}`,
    };
    const currentRules = watchRules || [];
    setValue('automationRules', [...currentRules, newRule]);
    setSelectedRuleId(newRule.id!);
    setSaveError(null);
    // Track current tab so we can navigate back on cancel
    const currentPath = location.pathname;
    setCreateSourceTab(currentPath);

    // If on History tab, navigate to Automations tab first
    if (currentPath.endsWith('/history')) {
      navigate('/automations/rules', { replace: true });
    }

    // Open modal after navigation (or immediately if already on Automations tab)
    setIsRuleEditorModalOpen(true);
  };

  const handleUseRecipe = (recipe: PopularRecipe) => {
    resetForm({ automationRules: watchRules || [] });
    const base = DEFAULT_RULE();
    const conditions =
      recipe.triggerEvent === 'client_tag_added'
        ? {
            logic: 'AND' as const,
            rules: [
              {
                field: 'tag_id',
                operator: 'eq',
                value: '',
              },
            ],
          }
        : base.conditions;
    const actions = (recipe.actions || []).map((a) => ({
      type: a.type,
      params: a.params
        ? Object.entries(a.params).map(([key, value]) => ({ key, value }))
        : [],
    }));
    const newRule: AutomationRulePartial = {
      ...base,
      id: `temp-${nanoid()}`,
      name: recipe.title,
      description: recipe.description || '',
      triggerEvent: recipe.triggerEvent,
      conditions: conditions ?? { logic: 'AND', rules: [] },
      actions,
      enabled: true,
    };
    setValue('automationRules', [...(watchRules || []), newRule]);
    setSelectedRuleId(newRule.id!);
    setIsRuleEditorModalOpen(true);
    setSaveError(null);
  };

  const normalizeConditions = (rule: AutomationRulePartial) => ({
    logic: rule.conditions?.logic || 'AND',
    rules: (rule.conditions?.rules || []).map((condition) => ({
      field: condition.field,
      operator: condition.operator,
      value: condition.value,
    })),
  });

  const normalizeActions = (rule: AutomationRulePartial) =>
    (rule.actions || [])
      .filter((action) => action?.type && action.type !== 'call_webhook')
      .map((action) => {
        // Handle both array and Record param formats
        let normalizedParams: Array<{ key: string; value: string }>;
        if (!action.params) {
          normalizedParams = [];
        } else if (Array.isArray(action.params)) {
          normalizedParams = action.params.map((param) => ({
            key: param.key || '',
            value: param.value || '',
          }));
        } else {
          // Convert Record<string, string> to array format
          normalizedParams = Object.entries(action.params).map(
            ([key, value]: [string, string]) => ({
              key,
              value: value || '',
            }),
          );
        }
        return {
          type: action.type,
          params: normalizedParams,
        };
      });

  const mapCreateInput = (
    rule: AutomationRulePartial,
  ): CreateAutomationRuleInput => ({
    name: rule.name.trim() || 'Untitled Automation',
    description: rule.description || '',
    enabled: rule.enabled ?? true,
    triggerEvent: (rule.triggerEvent ||
      'assessment_completed') as AutomationRuleTriggerEvent,
    triggerConfig: rule.triggerConfig || null,
    conditions: normalizeConditions(rule),
    actions: normalizeActions(rule) as CreateAutomationRuleInput['actions'],
    priority: rule.priority ?? 100,
  });

  /**
   * Maps a rule form state to the mutation input format.
   * Uses server ID if available (saved rules), otherwise falls back to client ID.
   * Note: New rules (with temp- IDs) should use create mutation, not update.
   */
  const mapUpdateInput = (
    rule: AutomationRulePartial,
  ): UpdateAutomationRuleInput => ({
    automationRuleId: rule.automationRuleId || rule.id!,
    name: rule.name.trim() || 'Untitled Automation',
    description: rule.description || '',
    enabled: rule.enabled ?? true,
    triggerEvent: (rule.triggerEvent ||
      'assessment_completed') as AutomationRuleTriggerEvent,
    triggerConfig: rule.triggerConfig || null,
    conditions: normalizeConditions(rule),
    actions: normalizeActions(rule) as UpdateAutomationRuleInput['actions'],
    priority: rule.priority ?? 100,
  });

  const updateRuleInForm = (nextRule: AutomationRulePartial) => {
    setValue(
      'automationRules',
      (watchRules || []).map((rule) =>
        rule.id === nextRule.id ? nextRule : rule,
      ),
    );
  };

  const getRuleIds = (rule: AutomationRulePartial) => ({
    persistedId: rule.automationRuleId || rule.id || null,
    localId: rule.id || rule.automationRuleId || null,
  });

  const handleToggleRule = async (rule: AutomationRulePartial) => {
    const { persistedId, localId } = getRuleIds(rule);
    const automationRuleId = persistedId;

    // Show user feedback for unsaved rules instead of silent return
    if (!automationRuleId || automationRuleId.startsWith('temp-')) {
      customToast.error('Save the rule first before enabling/disabling.');
      return;
    }

    try {
      if (rule.enabled) {
        await disableAutomationRuleMutation({
          variables: { automationRuleId },
        });
      } else {
        await enableAutomationRuleMutation({
          variables: { automationRuleId },
        });
      }

      // Only update local state after successful mutation
      if (localId) {
        setValue(
          'automationRules',
          (watchRules || []).map((existingRule) =>
            existingRule.id === localId
              ? {
                  ...existingRule,
                  enabled: !rule.enabled,
                }
              : existingRule,
          ),
        );
      }
      customToast.success(
        rule.enabled ? 'Automation disabled.' : 'Automation enabled.',
      );
      refetchAutomationRules();
    } catch (error) {
      // Log to Sentry for monitoring
      logger.error('Failed to toggle automation rule', {
        ruleId: automationRuleId,
        wasEnabled: rule.enabled,
        error: error instanceof Error ? error : new Error(String(error)),
      });
      customToast.error(
        error instanceof Error
          ? error.message
          : 'Unable to update automation status.',
      );
    }
  };

  const handleEditRule = (rule: AutomationRulePartial) => {
    if (
      !(watchRules || []).find((existingRule) => existingRule.id === rule.id)
    ) {
      setValue('automationRules', [...(watchRules || []), rule]);
    }
    setSelectedRuleId(rule.id!);
    setIsRuleEditorModalOpen(true);
    setSaveError(null);
  };

  const handleDeleteRule = (ruleId: string) => {
    setDeleteTargetRuleId(ruleId);
  };

  const handleConfirmDeleteRule = async () => {
    if (!deleteTargetRuleId) return;

    const targetRule = (watchRules || []).find(
      (rule) =>
        rule.id === deleteTargetRuleId ||
        rule.automationRuleId === deleteTargetRuleId,
    );
    const effectiveRuleId =
      targetRule?.automationRuleId || targetRule?.id || deleteTargetRuleId;
    const selectedRuleMatchId = targetRule?.id || effectiveRuleId;

    if (effectiveRuleId.startsWith('temp-')) {
      setValue(
        'automationRules',
        (watchRules || []).filter((rule) => rule.id !== effectiveRuleId),
      );
      if (selectedRuleId === selectedRuleMatchId) {
        setSelectedRuleId(null);
      }
      return;
    }

    try {
      await deleteAutomationRuleMutation({
        variables: { automationRuleId: effectiveRuleId },
        onCompleted: ({ deleteAutomationRule }) => {
          if (!deleteAutomationRule) {
            customToast.error('Unable to delete automation.');
            return;
          }

          setValue(
            'automationRules',
            (watchRules || []).filter(
              (rule) =>
                rule.id !== effectiveRuleId &&
                rule.automationRuleId !== effectiveRuleId,
            ),
          );
          if (selectedRuleId === selectedRuleMatchId) {
            setSelectedRuleId(null);
          }
          refetchAutomationRules();
          customToast.success('Automation deleted.');
        },
        onError: (error) => {
          // Log to Sentry for monitoring
          logger.error('Failed to delete automation rule', {
            ruleId: effectiveRuleId,
            error,
          });
          customToast.error(error.message || 'Unable to delete automation.');
        },
      });
    } catch (error) {
      logger.error('Unexpected error during automation rule deletion', {
        ruleId: effectiveRuleId,
        error: error instanceof Error ? error : new Error(String(error)),
      });
      customToast.error('Unable to delete automation.');
    }
  };

  const handleSaveRule = (rule: AutomationRulePartial) => {
    setSaveError(null);
    const persistedRule = automationRulesData?.automationRules?.some(
      (existingRule) => existingRule.id === rule.id,
    );

    if (persistedRule) {
      updateAutomationRuleMutation({
        variables: {
          input: mapUpdateInput(rule),
        },
        onCompleted: async ({ updateAutomationRule }) => {
          setIsSavingRefetch(true);
          try {
            await Promise.all([refetchAutomationRules(), refetchProgramTags()]);
            setSelectedRuleId(updateAutomationRule.id);
            setSaveError(null);
            setIsRuleEditorModalOpen(false);
            customToast.success('Automation saved.');
          } catch (error) {
            // Refetch failed but the mutation succeeded - still close the modal
            logger.error('Failed to refetch after update', { error });
            setSelectedRuleId(updateAutomationRule.id);
            setSaveError(null);
            setIsRuleEditorModalOpen(false);
            customToast.success('Automation saved.');
          } finally {
            setIsSavingRefetch(false);
          }
        },
        onError: (error) => {
          const message = error.message || 'Failed to update automation.';
          // Log to Sentry for monitoring
          logger.error('Failed to update automation rule', {
            ruleId: rule.id,
            triggerEvent: rule.triggerEvent,
            error,
          });
          setSaveError(message);
          customToast.error(message);
        },
      });
    } else {
      createAutomationRuleMutation({
        variables: { input: mapCreateInput(rule) },
        onCompleted: async ({ createAutomationRule }) => {
          setIsSavingRefetch(true);
          try {
            await Promise.all([refetchAutomationRules(), refetchProgramTags()]);
            setSelectedRuleId(createAutomationRule.id);
            setSaveError(null);
            setIsRuleEditorModalOpen(false);
            // Navigate to Automations tab after successful creation
            navigate('/automations/rules', { replace: true });
            customToast.success('Automation created.');
          } catch (error) {
            // Refetch failed but the mutation succeeded - still close the modal and navigate
            logger.error('Failed to refetch after create', { error });
            setSelectedRuleId(createAutomationRule.id);
            setSaveError(null);
            setIsRuleEditorModalOpen(false);
            navigate('/automations/rules', { replace: true });
            customToast.success('Automation created.');
          } finally {
            setIsSavingRefetch(false);
            setCreateSourceTab(null);
          }
        },
        onError: (error) => {
          const message = error.message || 'Failed to create automation.';
          // Log to Sentry for monitoring
          logger.error('Failed to create automation rule', {
            ruleName: rule.name,
            triggerEvent: rule.triggerEvent,
            error,
          });
          setSaveError(message);
          customToast.error(message);
        },
      });
    }
  };

  const selectedRule = useMemo(() => {
    if (!selectedRuleId) return null;
    return (watchRules || []).find((r) => r.id === selectedRuleId) || null;
  }, [selectedRuleId, watchRules]);

  const handleCloseRuleEditor = () => {
    if (selectedRule?.id?.startsWith('temp-')) {
      setValue(
        'automationRules',
        (watchRules || []).filter((r) => r.id !== selectedRule?.id),
      );
      setSelectedRuleId(null);
    }
    setIsRuleEditorModalOpen(false);
    // If user was on another tab when they clicked Create, stay there (don't navigate)
    if (createSourceTab && createSourceTab !== location.pathname) {
      navigate(createSourceTab, { replace: true });
      setCreateSourceTab(null);
    }
  };

  if (!isAuthorized) return null;

  return (
    <div
      className={classNames(
        'flex flex-col overflow-hidden',
        showUpgradeBanner
          ? 'h-[calc(100vh-var(--top-nav-height)-var(--upgrade-banner-height))]'
          : 'h-[calc(100vh-var(--top-nav-height))]',
      )}
    >
      <div className="flex min-h-0 flex-1 flex-col px-20">
        {/* Fixed header - does not scroll */}
        <div className="shrink-0 bg-white pt-8 pb-4">
          <div className="mb-4 flex items-center justify-between gap-x-10">
            <div className="mr-4 flex flex-col gap-y-3">
              <h1 className="mb-1 font-serif text-subtitle text-neutral-150">
                Automations
              </h1>
              <div className="text-neutral-120 max-w-xl text-body">
                Set up automated workflows to save time and ensure consistent
                communication with your clients.
              </div>
            </div>
            <Button
              theme="primary"
              size="medium"
              title="Create automation"
              IconComponent={PlusSmIcon}
              iconPosition="left"
              onClick={handleCreateNew}
              className={classNames(
                (isTemplatesTab || isHistoryTab || isConfigurationTab) &&
                  'invisible',
              )}
            />
          </div>

          {/* Show error if queries fail to load */}
          {automationRulesError && (
            <div className="mb-4 rounded-md border border-red-100 bg-red-25 p-4 text-red-150">
              <p className="font-semibold">Failed to load automations</p>
              <p className="text-sm mt-1">
                Please refresh the page. If the problem persists, contact
                support.
              </p>
            </div>
          )}
          {executionsError && (
            <div className="mb-4 rounded-md border border-red-100 bg-red-25 p-4 text-red-150">
              <p className="font-semibold">Failed to load execution history</p>
              <p className="text-sm mt-1">
                Please refresh the page. If the problem persists, contact
                support.
              </p>
            </div>
          )}

          <Tabs
            tabList={[
              { name: 'Automations', route: 'rules' },
              { name: 'History', route: 'history' },
              { name: 'Templates', route: 'templates' },
              {
                name: 'Webhooks',
                route: 'configuration',
                disabled: true,
                badge: 'Coming soon',
              },
            ]}
            fullWidth
            containerClassName="mb-0"
          />
        </div>

        {/* PageContainer only wraps the content, not the header */}
        <PageContainer
          noPadding
          containerClassName={classNames(
            'flex min-h-0 flex-1 flex-col',
            (isHistoryTab || isConfigurationTab || isTemplatesTab) &&
              'overflow-y-auto scrollbar-hide',
          )}
        >
          {isTemplatesTab ? (
            <Templates />
          ) : isConfigurationTab ? (
            <ConfigurationTab />
          ) : isHistoryTab ? (
            <ExecutionsTab
              rules={(watchRules || []) as AutomationRulePartial[]}
            />
          ) : automationRulesLoading ? (
            <div className="flex min-h-[400px] flex-1 items-center justify-center">
              <Spinner className="h-8 w-8 animate-spin text-green-150" />
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="flex min-h-0 flex-1 gap-6">
                {/* Fixed Sidebar - does not scroll */}
                <div className="flex w-64 shrink-0 flex-col border-r border-neutral-100 pr-4">
                  <div className="space-y-2">
                    <button
                      onClick={() => setActiveSection('recipes')}
                      className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-body transition-colors ${
                        activeSection === 'recipes'
                          ? 'bg-secondary-50 font-medium text-neutral-150'
                          : 'text-neutral-120 hover:bg-neutral-50 hover:text-neutral-150'
                      }`}
                    >
                      <SparklesIcon className="h-5 w-5 shrink-0" aria-hidden />
                      Recipes
                    </button>
                    <button
                      onClick={() => setActiveSection('automations')}
                      className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-body transition-colors ${
                        activeSection === 'automations'
                          ? 'bg-secondary-50 font-medium text-neutral-150'
                          : 'text-neutral-120 hover:bg-neutral-50 hover:text-neutral-150'
                      }`}
                    >
                      <LightningBoltIcon
                        className="h-5 w-5 shrink-0"
                        aria-hidden
                      />
                      Your automations
                    </button>
                  </div>
                </div>

                {/* Main Content Area - only cards scroll */}
                <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                  {activeSection === 'recipes' && (
                    <section className="flex min-h-0 flex-1 flex-col">
                      <div className="mb-3 flex shrink-0 items-center justify-between gap-3">
                        <h2 className="font-sans text-category font-semibold text-neutral-150">
                          All Recipes
                        </h2>
                      </div>
                      <div className="relative flex min-h-0 flex-1 flex-col">
                        <div
                          className={classNames(
                            'pointer-events-none absolute left-0 right-0 top-0 z-10 h-8 bg-gradient-to-b from-white to-transparent transition-opacity duration-200',
                            !showFade && 'opacity-0',
                          )}
                          aria-hidden
                        />
                        <div
                          className="min-h-0 flex-1 overflow-y-auto pb-4 scrollbar-hide"
                          onScroll={(e) =>
                            setShowFade(e.currentTarget.scrollTop > 0)
                          }
                        >
                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {POPULAR_RECIPES.map((recipe) => (
                              <PopularRecipeCard
                                key={recipe.id}
                                recipe={recipe}
                                onUseRecipe={handleUseRecipe}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </section>
                  )}

                  {activeSection === 'automations' && (
                    <section className="flex min-h-0 flex-1 flex-col">
                      <h2 className="mb-3 shrink-0 font-sans text-category font-semibold text-neutral-150">
                        Your Automations
                      </h2>
                      <div className="relative flex min-h-0 flex-1 flex-col">
                        <div
                          className={classNames(
                            'pointer-events-none absolute left-0 right-0 top-0 z-10 h-8 bg-gradient-to-b from-white to-transparent transition-opacity duration-200',
                            !showFade && 'opacity-0',
                          )}
                          aria-hidden
                        />
                        <div
                          className="min-h-0 flex-1 overflow-y-auto scrollbar-hide"
                          onScroll={(e) =>
                            setShowFade(e.currentTarget.scrollTop > 0)
                          }
                        >
                          <RuleList
                            automationRules={watchRules as AutomationRule[]}
                            executionStatsByRuleId={executionStatsByRuleId}
                            selectedRuleId={selectedRuleId}
                            onCreateRule={handleCreateNew}
                            onSelectRule={handleSelectRule}
                            onToggleRule={handleToggleRule}
                            onEditRule={handleEditRule}
                            onDeleteRule={handleDeleteRule}
                            showHeader={false}
                            newlyCreatedTags={newlyCreatedTags}
                          />
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              </div>

              {isRuleEditorModalOpen && selectedRule && (
                <Modal
                  isOpen={isRuleEditorModalOpen}
                  setClosed={handleCloseRuleEditor}
                  width="large"
                  overflow="auto"
                  className="!w-[67.5%] !max-w-[825px]"
                  closeRowText={
                    selectedRule?.id?.startsWith('temp-')
                      ? 'Create New Automation'
                      : 'Edit Automation'
                  }
                  closeRowClassName="mb-6 text-category font-semibold"
                >
                  <RuleEditor
                    rule={selectedRule}
                    onChange={updateRuleInForm}
                    onSave={handleSaveRule}
                    onCancel={handleCloseRuleEditor}
                    updating={updating || creating || isSavingRefetch}
                    saveError={saveError}
                    onTagCreated={(tag) =>
                      setNewlyCreatedTags((prev) => ({
                        ...prev,
                        [tag.id]: tag,
                      }))
                    }
                  />
                </Modal>
              )}
            </div>
          )}
        </PageContainer>
      </div>
      <ConfirmDeleteModal
        isOpen={Boolean(deleteTargetRuleId)}
        setClosed={() => setDeleteTargetRuleId(null)}
        title="Delete automation?"
        actionButtonTitle="Delete"
        fetching={deleting}
        performDelete={handleConfirmDeleteRule}
      >
        This cannot be undone.
      </ConfirmDeleteModal>
    </div>
  );
};

export default Automations;
